import type { DrawingSettings, StrokePoint } from "../canvas/canvasTypes";
import type { ComicBubbleObject, Rect } from "../comic/comicTypes";
import type { PersistedLayerState } from "../layers/layerTypes";
import type { ImmutableRasterPixelBuffer } from "../selection/RasterPixelBuffer";
import type { ShapeGeometry, ShapeStyle, ShapeTool } from "../shapes/shapeTypes";
import type { FillTolerance, RgbaTuple } from "../fill/fillTypes";
import type {
  IntegerRect,
  RasterSelectionAction,
  RasterSelectionTransform,
} from "../selection/selectionTypes";

export interface StrokeCommand {
  readonly kind: "stroke";
  readonly id: string;
  readonly layerId: string;
  readonly settings: Readonly<DrawingSettings>;
  readonly points: ReadonlyArray<Readonly<StrokePoint>>;
  readonly panelId?: string;
  readonly clipRect?: Readonly<Rect>;
}

export interface AddComicObjectCommand {
  readonly kind: "addComicObject";
  readonly id: string;
  readonly object: Readonly<ComicBubbleObject>;
}

export interface TransformComicObjectCommand {
  readonly kind: "transformComicObject";
  readonly id: string;
  readonly before: Readonly<ComicBubbleObject>;
  readonly after: Readonly<ComicBubbleObject>;
}

export interface EditComicTextCommand {
  readonly kind: "editComicText";
  readonly id: string;
  readonly objectId: string;
  readonly beforeText: string;
  readonly afterText: string;
}

export interface DeleteComicObjectCommand {
  readonly kind: "deleteComicObject";
  readonly id: string;
  readonly object: Readonly<ComicBubbleObject>;
}

export interface ClearPanelCommand {
  readonly kind: "clearPanel";
  readonly id: string;
  readonly panelId: string;
}

export interface DuplicatePanelCommand {
  readonly kind: "duplicatePanel";
  readonly id: string;
  readonly sourcePanelId: string;
  readonly targetPanelId: string;
  readonly duplicatedObjects: ReadonlyArray<Readonly<ComicBubbleObject>>;
}

export type LayerMutationAction =
  | "addLayer"
  | "deleteLayer"
  | "renameLayer"
  | "reorderLayer"
  | "visibilityLayer"
  | "lockLayer"
  | "opacityLayer";

export interface LayerStateSnapshot {
  readonly activeLayerId: string;
  readonly layers: ReadonlyArray<Readonly<PersistedLayerState>>;
}

export interface LayerMutationCommand {
  readonly kind: "layerMutation";
  readonly id: string;
  readonly action: LayerMutationAction;
  readonly before: LayerStateSnapshot;
  readonly after: LayerStateSnapshot;
}

export interface ClearLayerCommand {
  readonly kind: "clearLayer";
  readonly id: string;
  readonly layerId: string;
}

export interface MergeLayerDownCommand {
  readonly kind: "mergeLayerDown";
  readonly id: string;
  readonly sourceLayerId: string;
  readonly targetLayerId: string;
  readonly sourceOpacity: number;
  readonly targetOpacity: number;
  readonly before: LayerStateSnapshot;
  readonly after: LayerStateSnapshot;
}

export interface RasterSelectionCommand {
  readonly kind: "rasterSelection";
  readonly id: string;
  readonly action: RasterSelectionAction;
  readonly layerId: string;
  readonly sourceRect: IntegerRect;
  readonly sourcePixels: ImmutableRasterPixelBuffer;
  readonly destination?: RasterSelectionTransform;
  readonly clearSource: boolean;
  readonly clipRect: IntegerRect;
  readonly estimatedBytes: number;
}

export interface ShapeCommand {
  readonly kind: "shape";
  readonly id: string;
  readonly layerId: string;
  readonly tool: ShapeTool;
  readonly geometry: ShapeGeometry;
  readonly style: ShapeStyle;
  readonly clipRect: Readonly<Rect>;
  readonly panelId?: string;
  readonly estimatedBytes: number;
}

export interface FillCommand {
  readonly kind: "fill";
  readonly id: string;
  readonly layerId: string;
  readonly x: number;
  readonly y: number;
  readonly target: RgbaTuple;
  readonly replacementColour: string;
  readonly replacementOpacity: number;
  readonly tolerance: FillTolerance;
  readonly clipRect: Readonly<Rect>;
  readonly panelId?: string;
  readonly estimatedBytes: number;
}

export type HistoryCommand =
  | StrokeCommand
  | AddComicObjectCommand
  | TransformComicObjectCommand
  | EditComicTextCommand
  | DeleteComicObjectCommand
  | ClearPanelCommand
  | DuplicatePanelCommand
  | LayerMutationCommand
  | ClearLayerCommand
  | MergeLayerDownCommand
  | RasterSelectionCommand
  | ShapeCommand
  | FillCommand;

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  commandCount: number;
  pointCount: number;
  estimatedBytes: number;
  wasTrimmed: boolean;
}

export interface HistoryLimits {
  maxCommands: number;
  maxPoints: number;
  maxEstimatedBytes: number;
}

export const DEFAULT_HISTORY_LIMITS: Readonly<HistoryLimits> = {
  maxCommands: 80,
  maxPoints: 40_000,
  maxEstimatedBytes: 3_000_000,
};
